import { useRef, useState } from 'react'
import Supercluster from 'supercluster'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { BBox } from 'geojson'
import { dequal } from 'dequal'

export interface UseSuperclusterArguments<firstParam, secondParm> {
  geoJsonPoints: Array<Supercluster.PointFeature<firstParam>>
  bounds: BBox
  zoom: number
  options?: Supercluster.Options<firstParam, secondParm>
}

const useSupercluster = <firstParam, secondParm>({
  geoJsonPoints,
  bounds,
  zoom,
  options,
}: UseSuperclusterArguments<firstParam, secondParm>) => {
  const superclusterRef = useRef<Supercluster<firstParam, secondParm>>()

  const geoJsonPointsRef =
    useRef<Array<Supercluster.PointFeature<firstParam>>>()

  const [clusters, setClusters] = useState<
    Array<
      | Supercluster.ClusterFeature<secondParm>
      | Supercluster.PointFeature<firstParam>
    >
  >([])

  const zoomInt = Math.round(zoom)

  useDeepCompareEffect(() => {
    if (
      !superclusterRef.current ||
      !dequal(geoJsonPointsRef.current, geoJsonPoints) ||
      !dequal(
        (
          superclusterRef.current as typeof superclusterRef.current & {
            options: typeof options
          }
        ).options,
        options
      )
    ) {
      superclusterRef.current = new Supercluster(options) // If listings change - generate new super cluster
      superclusterRef.current.load(geoJsonPoints)
    }

    if (bounds && superclusterRef.current) {
      setClusters(superclusterRef.current.getClusters(bounds, zoomInt)) // As bounds change - fetch new clusters
    }

    geoJsonPointsRef.current = geoJsonPoints
  }, [bounds, geoJsonPoints, options, zoomInt])

  return { clusters, supercluster: superclusterRef.current }
}

export default useSupercluster
